import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui';
import CallInGhl from '../components/CallInGhl';
import {
  api, type GhlEmailCampaign, type TrackedCampaign, type TrackedMember, type TrackerBoardAnswer, type TrackerResults,
  type TrackerSectionKey,
} from '../lib/api';

/**
 * CAMP1 (2026-10-27) - the Campaign tracker. GoHighLevel sends the campaign; this page tracks what came back:
 * replies (his Gmail for email, GoHighLevel for texts), the hot list, unsubscribes, who is due a follow-up, who the
 * campaign has not reached yet, and the results.
 * ⛔ Nothing here writes to GoHighLevel or Gmail. "Track a campaign" saves a row in the app only.
 * ⛔ Never "opened" for an email (nothing measures it yet), and GoHighLevel's failed count is the campaign's, not a person's.
 */

const POLL_MS = 4000;
const DAY = 86_400_000;

const TITLE: Record<TrackerSectionKey, string> = {
  replied: 'Replied',
  hot: 'Hot',
  unsubscribed: 'Unsubscribed',
  follow_up: 'Follow-up due',
  not_sent: 'Not sent yet',
  waiting: 'Waiting',
};
const HINT: Record<TrackerSectionKey, string> = {
  replied: 'Wrote back within 14 days of the send - newest first.',
  hot: 'Replied, opened their portal twice or more, or downloaded a document - call these first.',
  unsubscribed: 'Email turned off in GoHighLevel - never send them another campaign.',
  follow_up: 'Sent three or more days ago, nothing back yet.',
  not_sent: 'On the tag, but GoHighLevel has not sent them this campaign yet (a drip, or a failed send).',
  waiting: 'Sent in the last three days.',
};

function when(iso: string | null | undefined, now: number): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const date = new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/Los_Angeles' });
  const days = Math.floor((now - t) / DAY);
  return days <= 0 ? `${date} (today)` : days === 1 ? `${date} (yesterday)` : `${date} (${days}d ago)`;
}

/** "+19095550101" -> "(909) 555-0101"; anything else as GoHighLevel has it. */
function phoneWords(phone: string): string {
  const d = phone.replace(/\D/g, '');
  const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone;
}

function MemberCard({ m, section, now }: { m: TrackedMember; section: TrackerSectionKey; now: number }) {
  return (
    <li className="min-w-0 rounded-lg border border-gray-200 bg-white p-4" data-testid="member-row">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="break-words text-sm font-semibold text-gray-900">{m.name ?? m.email ?? 'No name in GoHighLevel'}</span>
          {m.email && <div className="break-all text-xs text-gray-500">{m.email}</div>}
        </div>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {m.phone && <span className="text-xs tabular-nums text-gray-500">{phoneWords(m.phone)}</span>}
          {m.dnd_call && <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Do not call</span>}
          <CallInGhl url={m.ghl_contact_url} />
        </div>
      </div>

      {section === 'hot' && m.hot_reasons.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {m.hot_reasons.map((r) => (
            <span key={r} className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">{r}</span>
          ))}
        </div>
      )}

      {m.reply_at && section !== 'unsubscribed' && (
        <div className="mt-2 text-xs text-gray-600">
          <span className="font-medium text-gray-700">{m.reply_channel === 'sms' ? 'Text' : 'Email'} reply</span>, {when(m.reply_at, now)}
          {m.reply_subject && <span className="text-gray-500"> - {m.reply_subject}</span>}
          {m.reply_preview && <span className="block break-words text-gray-500">"{m.reply_preview}"</span>}
        </div>
      )}
      {section === 'unsubscribed' && m.unsubscribed_reason && (
        <p className="mt-2 text-xs text-gray-600">{m.unsubscribed_reason}</p>
      )}
      {(section === 'follow_up' || section === 'waiting') && m.sent_at && (
        <p className="mt-2 text-xs text-gray-600">Sent {when(m.sent_at, now)}{m.auto_reply_at ? ' - an auto-reply came back (away or out of office)' : ''}</p>
      )}
      {m.portal_views > 0 && section !== 'hot' && (
        <p className="mt-1 text-xs text-gray-600">Portal: {m.portal_views} visit{m.portal_views === 1 ? '' : 's'}{m.portal_downloads ? `, ${m.portal_downloads} download${m.portal_downloads === 1 ? '' : 's'}` : ''}</p>
      )}
      {m.read_state === 'unreadable' && (
        <p role="status" className="mt-2 text-xs text-red-700">Couldn't read this person from GoHighLevel last time - the page will try again.</p>
      )}
    </li>
  );
}

function Results({ r, mail, kind }: { r: TrackerResults; mail: TrackerBoardAnswer['mail']; kind: TrackedCampaign['kind'] }) {
  const repliedWords = mail === 'read' || mail === 'pending' ? String(r.replied) : `${r.replied} (texts only)`;
  const cells: Array<{ id: string; label: string; value: string }> = [
    { id: 'sent', label: 'Sent', value: String(r.sent) },
    { id: 'not-sent', label: 'Not sent yet', value: String(r.not_sent) },
    { id: 'failed', label: 'Failed (GoHighLevel count)', value: r.failed_in_ghl === null ? '-' : String(r.failed_in_ghl) },
    { id: 'replied', label: 'Replied', value: repliedWords },
    { id: 'hot', label: 'Hot', value: String(r.hot) },
    { id: 'unsubscribed', label: 'Unsubscribed', value: String(r.unsubscribed) },
  ];
  if (kind === 'portal') cells.push({ id: 'portal', label: 'Opened their portal', value: r.portal_opened === null ? '-' : String(r.portal_opened) });
  return (
    <dl className="mb-6 grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {cells.map((c) => (
        <div key={c.id} data-testid={`result-${c.id}`} className="min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2">
          <dt className="text-xs text-gray-500">{c.label}</dt>
          <dd className="text-lg font-semibold tabular-nums text-gray-900">{c.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Board({ id }: { id: string }) {
  const [board, setBoard] = useState<TrackerBoardAnswer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showNotSent, setShowNotSent] = useState(false);
  const timer = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const b = await api.campaignTracker.get(id);
      setBoard(b);
      setError(null);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : "Couldn't read this campaign - try again.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    if (board?.syncing) timer.current = window.setTimeout(() => void load(), POLL_MS);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [board, load]);

  const refresh = async () => {
    try { await api.campaignTracker.sync(id); } catch { /* the reload below says what is wrong */ }
    await load();
  };

  const now = board ? Date.parse(board.read_at) : Date.now();
  const c = board?.campaign;

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Link to="/campaign-tracker" className="text-xs text-gray-500 hover:text-gray-800">All campaigns</Link>
          <h1 className="break-words font-display text-[24px] font-semibold uppercase leading-[1.1] text-gray-900 sm:text-[28px]">
            {c?.name ?? 'Campaign'}
          </h1>
          {c && (
            <p className="mt-1 text-sm text-gray-500">
              Sent {when(c.sent_at, now)} to everyone tagged <span className="font-medium">{c.ghl_tag}</span>
              {c.subject ? <> - "{c.subject}"</> : null}
            </p>
          )}
        </div>
        <Button variant="outline" onClick={() => void refresh()} isLoading={loading}>Refresh</Button>
      </div>

      {error && (
        <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span className="min-w-0 break-words">{error}</span>
          <Button size="sm" variant="outline" onClick={() => void load()} isLoading={loading}>Try again</Button>
        </div>
      )}
      {!board && !error && <p className="py-16 text-center text-gray-500">Loading the campaign...</p>}

      {board && c && (
        <>
          {board.syncing && (
            <p role="status" aria-label="Reading" className="mb-3 text-xs text-gray-500">Reading GoHighLevel and your Gmail for replies...</p>
          )}
          {(board.mail === 'not_connected' || board.mail === 'unreadable') && (
            <p role="status" aria-label="Email replies" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              {board.mail === 'not_connected'
                ? 'Could not read email replies - your Gmail is not connected to the app. Text replies are still shown, and email replies found earlier are kept.'
                : 'Could not read email replies from Gmail this time - the ones found earlier are kept. Refresh to try again.'}
            </p>
          )}
          {c.sync_error && <p role="status" className="mb-3 text-xs text-red-700">The last read stopped: {c.sync_error}</p>}

          <Results r={board.results} mail={board.mail} kind={c.kind} />
          {board.results.reading > 0 && (
            <p className="mb-4 text-xs text-gray-500">Still reading {board.results.reading} {board.results.reading === 1 ? 'person' : 'people'} from GoHighLevel - they show up below as each is read.</p>
          )}

          {board.sections.filter((s) => s.members.length > 0).map((s) => (
            <section key={s.key} aria-label={TITLE[s.key]} className="mb-8">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{TITLE[s.key]} ({s.members.length})</h2>
              <p className="mb-3 text-xs text-gray-400">{HINT[s.key]}</p>
              {s.key === 'not_sent' && !showNotSent ? (
                <Button size="sm" variant="outline" onClick={() => setShowNotSent(true)}>
                  Show the {s.members.length} not sent yet
                </Button>
              ) : (
                <ul className="space-y-3">
                  {s.members.map((m) => <MemberCard key={`${s.key}-${m.id}`} m={m} section={s.key} now={now} />)}
                </ul>
              )}
            </section>
          ))}
          {board.results.members === 0 && !board.syncing && (
            <p className="py-10 text-center text-sm text-gray-500">Nobody is on the tag {c.ghl_tag} in GoHighLevel.</p>
          )}
        </>
      )}
    </>
  );
}

function TrackForm({ onMade }: { onMade: (id: string) => void }) {
  const [ghl, setGhl] = useState<GhlEmailCampaign[] | null>(null);
  const [scheduleId, setScheduleId] = useState('');
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [kind, setKind] = useState<'email' | 'portal'>('email');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.campaignTracker.ghlCampaigns()
      .then((r) => setGhl(r.campaigns))
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't read your GoHighLevel campaigns."));
  }, []);

  const pick = (id: string) => {
    setScheduleId(id);
    const s = ghl?.find((x) => x.id === id);
    if (s && !name.trim()) setName(s.name);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await api.campaignTracker.create({ name, ghl_tag: tag, ghl_schedule_id: scheduleId || null, kind });
      onMade(r.campaign.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save the campaign.");
    } finally {
      setSaving(false);
    }
  };

  const INPUT = 'mt-1 w-full min-w-0 rounded-md border border-gray-300 px-3 py-2 text-sm';
  return (
    <section aria-label="Track a campaign" className="mb-8 rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Track a campaign</h2>
      <p className="mb-3 text-xs text-gray-400">Pick the GoHighLevel Email Marketing campaign and the tag it went to. Nothing is sent or tagged - the app only reads.</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm text-gray-700">GoHighLevel campaign
          <select className={INPUT} value={scheduleId} onChange={(e) => pick(e.target.value)}>
            <option value="">{!ghl ? 'Loading...' : kind === 'portal' ? 'Not from Email Marketing (a portal send)' : 'Pick the campaign'}</option>
            {(ghl ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.name}{s.sentAt ? ` - ${new Date(s.sentAt).toLocaleDateString('en-US')}` : ''}{s.total !== null ? ` (${s.total})` : ''}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm text-gray-700">The tag it went to
          <input className={INPUT} value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. trucking-cold-tier1" />
        </label>
        <label className="block text-sm text-gray-700">Name on this page
          <input className={INPUT} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="block text-sm text-gray-700">Kind
          <select className={INPUT} value={kind} onChange={(e) => setKind(e.target.value === 'portal' ? 'portal' : 'email')}>
            <option value="email">Email only</option>
            <option value="portal">Email with a portal link</option>
          </select>
        </label>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      <div className="mt-3"><Button onClick={() => void save()} isLoading={saving} disabled={!tag.trim() || !name.trim() || (kind === 'email' && !scheduleId)}>Track it</Button></div>
    </section>
  );
}

function CampaignList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Array<TrackedCampaign & { results: TrackerResults }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.campaignTracker.list()
      .then((r) => setRows(r.campaigns))
      .catch((e) => setError(e instanceof Error ? e.message : "Couldn't read the campaigns."));
  }, []);
  const now = Date.now();

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-display text-[28px] font-semibold uppercase leading-[1.06] tracking-[-.01em] text-gray-900 sm:text-[32px]">Campaign tracker</h1>
          <p className="mt-1 text-sm text-gray-500">GoHighLevel sends; this shows who replied, who is hot, and who to follow up.</p>
        </div>
        <Button variant="outline" onClick={() => setAdding((a) => !a)}>{adding ? 'Close' : 'Track a campaign'}</Button>
      </div>
      {adding && <TrackForm onMade={(id) => navigate(`/campaign-tracker?id=${encodeURIComponent(id)}`)} />}
      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {!rows && !error && <p className="py-16 text-center text-gray-500">Loading...</p>}
      {rows && rows.length === 0 && <p className="py-10 text-center text-sm text-gray-500">No campaigns tracked yet.</p>}
      {rows && rows.length > 0 && (
        <ul className="space-y-3">
          {rows.map((c) => (
            <li key={c.id}>
              <Link to={`/campaign-tracker?id=${encodeURIComponent(c.id)}`} className="block min-w-0 rounded-lg border border-gray-200 bg-white p-4 hover:border-gray-400">
                <div className="break-words text-sm font-semibold text-gray-900">{c.name}</div>
                <div className="text-xs text-gray-500">Sent {when(c.sent_at, now)} - tag {c.ghl_tag}</div>
                <div className="mt-1 text-xs text-gray-600">
                  {c.results.sent} sent · {c.results.replied} replied · {c.results.hot} hot · {c.results.unsubscribed} unsubscribed
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function CampaignTracker() {
  const [params] = useSearchParams();
  const id = params.get('id');
  return (
    <Layout>
      <div className="mx-auto max-w-4xl">{id ? <Board id={id} /> : <CampaignList />}</div>
    </Layout>
  );
}
